import React from 'react'

import { lastDayOfMonth, parseISO, startOfMonth, subMonths } from 'date-fns'
import {
  InsightsHeadcountPageDocument,
  ReportsInsightsCycleEnum,
} from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateToString } from 'modules/core/lib/dates'
import company from 'modules/core/store/collections/companies'

import {
  CycleDateRangeWithSetters,
  useCycleDateRange,
} from 'modules/insights/hooks/useCycleDateRange'
import HeadcountEvolution from 'modules/insights/lib/metrics/headcountEvolution'
import { useInsightsContext } from 'modules/insights/pages/RootPage'

export const useHeadcountPageQuery = () => {
  const { filters } = useInsightsContext()

  const cycleDateRange = useCycleDateRange(() => {
    const currentDate = new Date()

    return {
      cycle: 'monthly',
      startDate: startOfMonth(subMonths(currentDate, 11)),
      endDate: lastDayOfMonth(currentDate),
    }
  })

  const { data, variables, loading, error } = useQuery(InsightsHeadcountPageDocument, {
    variables: {
      companyId: company.current.id,
      cycle: cycleDateRange.cycle as ReportsInsightsCycleEnum,
      startDate: dateToString(cycleDateRange.startDate, 'backend'),
      endDate: dateToString(cycleDateRange.endDate, 'backend'),

      // Filters
      genders: filters.genders?.filter((gender): gender is string => gender != null),
      withNoGender: filters.genders?.includes(null) ?? false,
      legalEntities: filters.legalEntities,
      workplaces: filters.workplaces,
      managers: filters.managers?.filter((manager): manager is number => manager != null),
      withNoManager: filters.managers?.includes(null) ?? false,
      teams: filters.teams,
      nationalities: filters.nationalities?.filter(
        (nationality): nationality is string => nationality != null
      ),
      withNoNationality: filters.nationalities?.includes(null) ?? false,
    },
  })

  // Cache last data to avoid showing a loading page when filters change
  const [lastData, setLastData] = React.useState(
    data && variables ? { data, variables } : undefined
  )

  useUnsafeEffect(() => {
    if (data && variables) {
      setLastData({ data, variables })
    }
  }, [data, variables])

  const currentData = data ? data : lastData?.data
  const currentVariables = data ? variables : lastData?.variables

  const currentCycleDateRange: CycleDateRangeWithSetters = {
    ...cycleDateRange, // We set the current values and setters as a default
    ...(currentVariables
      ? {
          cycle: currentVariables.cycle,
          startDate: parseISO(currentVariables.startDate),
          endDate: parseISO(currentVariables.endDate),
        }
      : {}),
  }

  const headcountEvolution = currentData
    ? new HeadcountEvolution(
        currentData.contracts.monthlyHeadcounts,
        currentData.contracts.leavers,
        currentData.contracts.joiners,
        currentCycleDateRange
      )
    : undefined

  return {
    headcountEvolution,
    loading,
    error,
    cycleDateRange: currentCycleDateRange,
  }
}
